import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { HiChevronDown, HiOutlineMenu, HiOutlineShoppingCart, HiOutlineX } from 'react-icons/hi';
import {
  FiAward,
  FiBook,
  FiBookOpen,
  FiBriefcase,
  FiDownload,
  FiGift,
  FiMapPin,
  FiPackage,
  FiShare2,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import { api } from '@/lib/api/client';
import { LANGS, setLang, type LangCode } from '@/lib/i18n';
import { categoryLabel } from '@/lib/i18n/categoryLabel';
import { useAuth } from '@/features/auth/AuthContext';
import { useCart } from '@/features/commerce/CartContext';
import { NotificationBell } from '@/components/layout/public/NotificationBell';
import { SearchBox } from '@/components/layout/public/SearchBox';
import { CategoryIcon } from '@/components/common/CategoryIcon';
import type { CategoryMeta } from '@/features/catalog/course';

type HeaderLinkTarget =
  | { kind: 'route'; to: string }
  | { kind: 'anchor'; to: string; anchor: string; pendingPhase: number };

const route = (to: string): HeaderLinkTarget => ({ kind: 'route', to });

interface MegaMenuLink {
  id: string;
  labelKey: string;
  hintKey?: string;
  target: HeaderLinkTarget;
  Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
}

const MEGA_MENU: {
  id: string;
  labelKey: string;
  cols: { headingKey: string; items: MegaMenuLink[] | 'TRACKS' }[];
}[] = [
  {
    id: 'courses',
    labelKey: 'nav.mega.courses.label',
    cols: [
      {
        headingKey: 'nav.mega.courses.browse_heading',
        items: [
          {
            id: 'All courses',
            labelKey: 'nav.mega.courses.all_courses',
            hintKey: 'nav.mega.courses.all_courses_hint',
            target: route('/courses'),
            Icon: FiBookOpen,
          },
          {
            id: 'Free courses',
            labelKey: 'nav.mega.courses.free_courses',
            hintKey: 'nav.mega.courses.free_courses_hint',
            target: route('/courses'),
            Icon: FiGift,
          },
          {
            id: 'Skill paths',
            labelKey: 'nav.mega.courses.skill_paths',
            hintKey: 'nav.mega.courses.skill_paths_hint',
            target: route('/courses'),
            Icon: FiTrendingUp,
          },
          {
            id: 'Course bundles',
            labelKey: 'nav.mega.courses.course_bundles',
            hintKey: 'nav.mega.courses.course_bundles_hint',
            target: route('/courses#bundleFilters'),
            Icon: FiPackage,
          },
        ],
      },
      { headingKey: 'nav.mega.courses.tracks_heading', items: 'TRACKS' },
    ],
  },
  {
    id: 'learn',
    labelKey: 'nav.mega.learn.label',
    cols: [
      {
        headingKey: 'nav.mega.learn.free_resources_heading',
        items: [
          {
            id: 'Free notes & guides',
            labelKey: 'nav.mega.learn.free_notes',
            hintKey: 'nav.mega.learn.free_notes_hint',
            target: route('/guides'),
            Icon: FiBook,
          },
          {
            id: 'Free downloads',
            labelKey: 'nav.mega.learn.free_downloads',
            hintKey: 'nav.mega.learn.free_downloads_hint',
            target: route('/resources'),
            Icon: FiDownload,
          },
        ],
      },
      {
        headingKey: 'nav.mega.learn.practice_heading',
        items: [
          {
            id: 'Leaderboard',
            labelKey: 'nav.mega.learn.leaderboard',
            hintKey: 'nav.mega.learn.leaderboard_hint',
            target: route('/leaderboard'),
            Icon: FiAward,
          },
          {
            id: 'Our instructors',
            labelKey: 'nav.mega.learn.instructors',
            hintKey: 'nav.mega.learn.instructors_hint',
            target: route('/instructors'),
            Icon: FiUsers,
          },
          {
            id: 'Verify a certificate',
            labelKey: 'nav.mega.learn.verify_certificate',
            hintKey: 'nav.mega.learn.verify_certificate_hint',
            target: route('/verify'),
            Icon: FiAward,
          },
        ],
      },
    ],
  },
  {
    id: 'more',
    labelKey: 'nav.mega.more.label',
    cols: [
      {
        headingKey: 'nav.mega.more.academy_heading',
        items: [
          {
            id: 'Learning centres',
            labelKey: 'nav.mega.more.learning_centres',
            hintKey: 'nav.mega.more.learning_centres_hint',
            target: route('/centres'),
            Icon: FiMapPin,
          },
          {
            id: 'Book store',
            labelKey: 'nav.mega.more.book_store',
            hintKey: 'nav.mega.more.book_store_hint',
            target: route('/store'),
            Icon: FiBook,
          },
          {
            id: 'For business',
            labelKey: 'nav.mega.more.for_business',
            hintKey: 'nav.mega.more.for_business_hint',
            target: route('/page/for-business'),
            Icon: FiBriefcase,
          },
          {
            id: 'Affiliate programme',
            labelKey: 'nav.mega.more.affiliate',
            hintKey: 'nav.mega.more.affiliate_hint',
            target: route('/affiliate'),
            Icon: FiShare2,
          },
        ],
      },
    ],
  },
];

function hrefOf(target: HeaderLinkTarget): string {
  return target.kind === 'anchor' ? `${target.to}#${target.anchor}` : target.to;
}

const navLinkClass =
  'relative rounded-lg px-3 py-2 text-sm font-medium text-[var(--ink-soft)] transition-colors hover:bg-[var(--paper)] hover:text-[var(--ink)]';

const dropdownLinkClass =
  'block rounded-lg px-3 py-2 text-sm font-medium text-[var(--ink)] transition-colors hover:bg-[var(--paper)] hover:text-[var(--cobalt)]';

function LangToggle() {
  const { i18n } = useTranslation();

  return (
    <div
      className="inline-flex rounded-full border border-[var(--line)] bg-[var(--paper)] p-0.5"
      role="group"
      aria-label="Language"
      data-testid="lang-toggle"
    >
      {LANGS.map((l) => {
        const active = i18n.resolvedLanguage === l.code;
        return (
          <button
            key={l.code}
            type="button"
            className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
              active ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-soft)] hover:text-[var(--ink)]'
            }`}
            data-testid={`lang-${l.code}`}
            aria-pressed={active}
            onClick={() => setLang(l.code as LangCode)}
          >
            {l.short}
          </button>
        );
      })}
    </div>
  );
}

function MegaMenuLinkRow({
  item,
  onClose,
}: {
  item: MegaMenuLink;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const Icon = item.Icon;
  const hint = item.hintKey ? t(item.hintKey) : null;

  return (
    <Link
      to={hrefOf(item.target)}
      data-testid={`nav-link-${item.id}`}
      data-pending={item.target.kind !== 'route' ? String(item.target.pendingPhase) : undefined}
      onClick={onClose}
      className="group flex items-start gap-3 rounded-xl px-2.5 py-2.5 text-[var(--ink)] no-underline transition-[background,transform] duration-150 hover:bg-[var(--paper)] max-[1130px]:px-2"
    >
      <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg bg-[linear-gradient(145deg,#fff5ef,#ffe8dd)] text-[var(--cobalt)] ring-1 ring-[rgba(244,80,42,.12)] transition group-hover:bg-[linear-gradient(145deg,#ffe8dd,#ffd8c4)]">
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-semibold leading-snug group-hover:text-[var(--cobalt)]">
          {t(item.labelKey)}
        </span>
        {hint ? (
          <span className="mt-0.5 block text-[12px] leading-snug text-[var(--ink-soft)]">{hint}</span>
        ) : null}
      </span>
    </Link>
  );
}

function MegaMenuDropdown({
  menu,
  open,
  onToggle,
  onClose,
  onHoverClose,
  categories,
}: {
  menu: (typeof MEGA_MENU)[number];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onHoverClose: () => void;
  categories: CategoryMeta[] | undefined;
}) {
  const { t } = useTranslation();
  const wide = menu.cols.length > 1;
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearCloseTimer() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <div
      className="relative max-[1130px]:w-full"
      onMouseEnter={() => {
        if (!window.matchMedia('(min-width: 1131px)').matches) return;
        clearCloseTimer();
        if (!open) onToggle();
      }}
      onMouseLeave={() => {
        if (!window.matchMedia('(min-width: 1131px)').matches) return;
        clearCloseTimer();
        closeTimer.current = setTimeout(() => {
          onHoverClose();
        }, 120);
      }}
    >
      <button
        type="button"
        className={`relative inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors max-[1130px]:w-full max-[1130px]:justify-between ${
          open
            ? 'bg-[var(--paper)] text-[var(--ink)]'
            : 'text-[var(--ink-soft)] hover:bg-[var(--paper)] hover:text-[var(--ink)]'
        }`}
        data-testid={`nav-drop-${menu.id}`}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        {t(menu.labelKey)}
        <HiChevronDown className={`size-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        <span
          className={`pointer-events-none absolute inset-x-3 -bottom-px hidden h-0.5 rounded-full bg-[linear-gradient(90deg,var(--ember),var(--cobalt))] transition-opacity min-[1131px]:block ${
            open ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden
        />
      </button>

      <div
        className={`${
          open ? 'flex' : 'hidden'
        } absolute top-full left-0 z-20 pt-3 max-[1130px]:static max-[1130px]:mt-1.5 max-[1130px]:pt-0`}
      >
        {/* pt-3 bridges the gap so the pointer can reach the panel without closing hover */}
        <div
          className={`relative flex w-full overflow-hidden rounded-2xl border border-[var(--line)] bg-white shadow-[0_18px_50px_rgba(28,20,12,.14)] max-[1130px]:flex-col max-[1130px]:gap-5 max-[1130px]:bg-[var(--paper-2)] max-[1130px]:p-3.5 max-[1130px]:shadow-none min-[1131px]:gap-0 ${
            wide ? 'min-[1131px]:min-w-[580px]' : 'min-[1131px]:min-w-[300px]'
          }`}
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 hidden h-px bg-gradient-to-r from-transparent via-[rgba(244,80,42,.45)] to-transparent min-[1131px]:block"
            aria-hidden
          />
          {menu.cols.map((col, colIdx) => {
            const isTracks = col.items === 'TRACKS';
            return (
              <div
                className={`min-w-0 p-4 max-[1130px]:w-full max-[1130px]:p-0 ${
                  isTracks
                    ? 'min-[1131px]:min-w-[260px] min-[1131px]:flex-[1.15]'
                    : 'min-[1131px]:min-w-[260px] min-[1131px]:flex-1'
                } ${
                  colIdx > 0 ? 'min-[1131px]:border-l min-[1131px]:border-[var(--line)] min-[1131px]:bg-[var(--paper-2)]' : ''
                }`}
                key={col.headingKey}
              >
                <p className="mb-2.5 px-2.5 text-[11px] font-bold tracking-[.14em] text-[var(--ink-soft)] uppercase max-[1130px]:px-1">
                  {t(col.headingKey)}
                </p>
                {isTracks ? (
                  <div className="grid grid-cols-1 gap-0.5">
                    {(categories ?? []).map((track) => (
                      <Link
                        key={track.slug}
                        to={`/courses?category=${track.slug}`}
                        data-testid={`nav-track-${track.slug}`}
                        onClick={onClose}
                        className="group flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-[13.5px] font-semibold text-[var(--ink)] no-underline transition hover:bg-white hover:text-[var(--cobalt)] max-[1130px]:hover:bg-[var(--paper)]"
                      >
                        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-white text-[var(--cobalt)] ring-1 ring-[var(--line)] transition group-hover:ring-[rgba(244,80,42,.25)] max-[1130px]:bg-[var(--card)]">
                          <CategoryIcon slug={track.slug} />
                        </span>
                        <span className="leading-snug whitespace-normal">{categoryLabel(track, t)}</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {(col.items as MegaMenuLink[]).map((item) => (
                      <MegaMenuLinkRow key={item.id} item={item} onClose={onClose} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NavLinksPanel({
  openDrop,
  setOpenDrop,
  closeMenus,
  categories,
  user,
}: {
  openDrop: string | null;
  setOpenDrop: (value: string | null | ((current: string | null) => string | null)) => void;
  closeMenus: () => void;
  categories: CategoryMeta[] | undefined;
  user: ReturnType<typeof useAuth>['user'];
}) {
  const { t } = useTranslation();

  return (
    <>
      {MEGA_MENU.map((menu) => (
        <MegaMenuDropdown
          key={menu.id}
          menu={menu}
          open={openDrop === menu.id}
          onToggle={() => setOpenDrop((current) => (current === menu.id ? null : menu.id))}
          onHoverClose={() => setOpenDrop((current) => (current === menu.id ? null : current))}
          onClose={closeMenus}
          categories={categories}
        />
      ))}

      <Link to="/store" data-testid="nav-store" className={navLinkClass} onClick={closeMenus}>
        {t('nav.store')}
      </Link>

      {!user ? (
        <>
          <Link
            to="/affiliate"
            data-testid="nav-join-affiliate-mobile"
            className={`${navLinkClass} font-semibold text-[var(--cobalt)] min-[1131px]:hidden`}
            onClick={closeMenus}
          >
            {t('nav.join_affiliate')}
          </Link>
          <div className="mt-4 hidden flex-col gap-2 border-t border-[var(--line)] pt-4 max-[760px]:flex">
            <Link
              to="/login"
              className="rounded-full border border-[var(--line)] px-4 py-2.5 text-center text-sm font-medium text-[var(--ink)] no-underline"
              data-testid="nav-login"
              onClick={closeMenus}
            >
              {t('nav.login')}
            </Link>
            <Link
              to="/register"
              className="rounded-full bg-gradient-to-r from-[var(--ember)] to-[var(--cobalt)] px-4 py-2.5 text-center text-sm font-semibold text-white no-underline"
              data-testid="nav-signup"
              onClick={closeMenus}
            >
              {t('nav.signup')}
            </Link>
          </div>
        </>
      ) : null}
    </>
  );
}

function ProfileMenu({
  user,
  open,
  onOpen,
  onClose,
  logout,
}: {
  user: NonNullable<ReturnType<typeof useAuth>['user']>;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  logout: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStaff = user.role !== 'student';
  const isInstructor = user.role === 'instructor' || user.role === 'mentor';
  const isAffiliate = user.role === 'affiliate';
  const isOrderManager = user.role === 'order_manager';
  const panelHome = isAffiliate
    ? '/partner'
    : isInstructor
      ? '/instructor'
      : isOrderManager
        ? '/order-manager'
        : '/admin';
  const panelLabel = isAffiliate
    ? t('nav.affiliate', { defaultValue: 'Affiliate' })
    : isInstructor
      ? t('nav.instructor', { defaultValue: 'Instructor' })
      : isOrderManager
        ? t('nav.orderManager', { defaultValue: 'Order Manager' })
        : t('nav.admin');

  function clearCloseTimer() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }

  function scheduleClose() {
    clearCloseTimer();
    closeTimer.current = setTimeout(onClose, 140);
  }

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <div
      className="relative"
      data-testid="nav-profile"
      onMouseEnter={() => {
        clearCloseTimer();
        onOpen();
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="grid size-9 place-items-center rounded-full bg-gradient-to-br from-neutral-800 to-neutral-500 text-xs font-bold text-white ring-2 ring-white transition hover:ring-[var(--cobalt)]"
        title={user.name}
        aria-label={user.name}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="nav-avatar"
        onClick={() => (open ? onClose() : onOpen())}
      >
        {user.avatarInitials ?? user.name.slice(0, 2).toUpperCase()}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute top-full right-0 z-[70] mt-2 min-w-[180px] rounded-xl border border-[var(--line)] bg-white p-1.5 shadow-[var(--shadow-lg)]"
          data-testid="nav-profile-menu"
        >
          <div className="border-b border-[var(--line)] px-3 py-2">
            <p className="truncate text-[13px] font-semibold text-[var(--ink)]">{user.name}</p>
            {user.email ? (
              <p className="truncate text-[11.5px] text-[var(--ink-soft)]">{user.email}</p>
            ) : null}
          </div>
          {isStaff ? (
            <Link
              to={panelHome}
              role="menuitem"
              className={dropdownLinkClass}
              data-testid="nav-admin"
              onClick={onClose}
            >
              {panelLabel}
            </Link>
          ) : (
            <Link
              to="/dashboard"
              role="menuitem"
              className={dropdownLinkClass}
              data-testid="nav-dashboard"
              onClick={onClose}
            >
              {t('nav.dashboard')}
            </Link>
          )}
          <button
            type="button"
            role="menuitem"
            className={`${dropdownLinkClass} w-full text-left text-[var(--coral)] hover:text-[var(--coral)]`}
            data-testid="nav-logout"
            onClick={() => {
              onClose();
              void logout();
            }}
          >
            {t('nav.logout')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Header({
  onOpenCart,
}: {
  onOpenAuth?: (mode: 'login' | 'signup') => void;
  onOpenCart: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [openDrop, setOpenDrop] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDrawerTop, setMobileDrawerTop] = useState(64);
  const navRef = useRef<HTMLElement>(null);

  const { data: cats } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<{ categories: CategoryMeta[] }>('/courses/meta/categories')).data,
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenDrop(null);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpenDrop(null);
        setMobileOpen(false);
      }
    }
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Promo banner sits above the sticky nav — fixed `top-16` would hide menu items under the header.
  useEffect(() => {
    function syncDrawerTop() {
      const bottom = navRef.current?.getBoundingClientRect().bottom;
      if (typeof bottom === 'number' && bottom > 0) setMobileDrawerTop(bottom);
    }
    syncDrawerTop();
    if (!mobileOpen) return;
    window.addEventListener('resize', syncDrawerTop);
    window.addEventListener('scroll', syncDrawerTop, { passive: true });
    const banner = document.querySelector('[data-testid="site-wide-banner"]');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncDrawerTop) : null;
    if (navRef.current) ro?.observe(navRef.current);
    if (banner) ro?.observe(banner);
    return () => {
      window.removeEventListener('resize', syncDrawerTop);
      window.removeEventListener('scroll', syncDrawerTop);
      ro?.disconnect();
    };
  }, [mobileOpen]);

  const closeMenus = () => {
    setOpenDrop(null);
    setMobileOpen(false);
  };

  return (
    <>
      <nav
        ref={navRef}
        className="sticky top-0 z-[60] border-b border-[var(--line)] bg-white/95 backdrop-blur-md supports-[backdrop-filter]:bg-white/80"
      >
        <div className="nav-inner container mx-auto flex h-24 items-center gap-4 px-5 lg:gap-6 lg:px-6">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex shrink-0 items-center"
          >
            <img src="/logo_1.png" alt="MAKTech Academy" className="h-22 w-auto object-contain" />
          </Link>

          <nav className="hidden min-[1131px]:flex min-[1131px]:flex-1 min-[1131px]:items-center min-[1131px]:gap-1">
            <NavLinksPanel
              openDrop={openDrop}
              setOpenDrop={setOpenDrop}
              closeMenus={closeMenus}
              categories={cats?.categories}
              user={user}
            />
          </nav>

          <SearchBox />

          <div id="navCta" className="ml-auto flex shrink-0 items-center gap-2 lg:gap-3">
          <button
            type="button"
            className="relative grid size-9 place-items-center rounded-lg text-[var(--ink-soft)] transition-colors hover:bg-[var(--paper)] hover:text-[var(--ink)]"
            data-testid="nav-cart"
            aria-label="Cart"
            onClick={onOpenCart}
          >
            <HiOutlineShoppingCart className="size-5" />
            {count > 0 ? (
              <span
                className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--coral)] px-1 text-[10px] font-bold text-white"
                data-testid="nav-cart-count"
              >
                {count}
              </span>
            ) : null}
          </button>

          <LangToggle />

          {!user ? (
            <Link
              key={`join-aff-${i18n.resolvedLanguage ?? i18n.language}`}
              to="/affiliate"
              data-testid="nav-join-affiliate"
              className="hidden rounded-full border border-[var(--line)] px-3.5 py-2 text-sm font-semibold text-[var(--ink)] transition-colors hover:border-[var(--cobalt)] hover:text-[var(--cobalt)] lg:inline-flex"
              onClick={closeMenus}
            >
              {t('nav.join_affiliate')}
            </Link>
          ) : null}

          {user ? (
            <>
              <NotificationBell />
              <ProfileMenu
                user={user}
                open={openDrop === 'profile'}
                onOpen={() => setOpenDrop('profile')}
                onClose={() => setOpenDrop((current) => (current === 'profile' ? null : current))}
                logout={logout}
              />
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden rounded-full border border-[var(--line)] px-4 py-2 text-sm font-medium text-[var(--ink)] no-underline transition-colors hover:border-[var(--cobalt)] hover:text-[var(--cobalt)] max-[760px]:hidden min-[761px]:inline-flex"
                data-testid="nav-login"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="hidden rounded-full bg-gradient-to-r from-[var(--ember)] to-[var(--cobalt)] px-4 py-2 text-sm font-semibold text-white no-underline shadow-[var(--shadow-orange)] transition hover:opacity-95 max-[760px]:hidden min-[761px]:inline-flex"
                data-testid="nav-signup"
              >
                {t('nav.signup')}
              </Link>
            </>
          )}
        </div>

          <button
            type="button"
            className="hamburger grid size-9 place-items-center rounded-lg text-[var(--ink)] transition-colors hover:bg-[var(--paper)] max-[1130px]:grid min-[1131px]:hidden"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => {
              setMobileOpen((open) => {
                if (open) return false;
                const bottom = navRef.current?.getBoundingClientRect().bottom;
                if (typeof bottom === 'number' && bottom > 0) setMobileDrawerTop(bottom);
                return true;
              });
            }}
          >
            {mobileOpen ? <HiOutlineX className="size-5" /> : <HiOutlineMenu className="size-5" />}
          </button>
        </div>
      </nav>

      {/* Outside sticky nav so fixed positioning is not clipped by backdrop-filter */}
      <nav
        id="navLinks"
        className={`${
          mobileOpen ? 'open flex' : 'hidden'
        } fixed inset-x-0 bottom-0 z-[55] flex-col gap-1 overflow-y-auto border-t border-[var(--line)] bg-white p-4 min-[1131px]:hidden`}
        style={mobileOpen ? { top: mobileDrawerTop } : undefined}
        aria-hidden={!mobileOpen}
      >
        <NavLinksPanel
          openDrop={openDrop}
          setOpenDrop={setOpenDrop}
          closeMenus={closeMenus}
          categories={cats?.categories}
          user={user}
        />
      </nav>
    </>
  );
}
